import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, AlertTriangle, Search } from "lucide-react";
import { matchesQuery } from "../components/ProductSearch";
import { api, errMsg } from "../lib/api";
import { CategoryManager } from "../components/CategoryManager";
import { AiFillPanel } from "../components/AiFillPanel";
import { StockTable } from "../components/stok/StockTable";
import { ProductDialog } from "../components/stok/ProductDialog";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";

const empty = {
  name: "",
  kind: "grocery",
  category: "",
  barcode: "",
  price: "",
  cost: "",
  stock: "",
  daily_stock: "",
  image_url: "",
  images: [],
  unit: "pcs",
  weight: "",
  low_stock_threshold: "5",
  is_new: false,
  description: "",
  description_ja: "",
  name_ja: "",
};

const formOf = (p) => ({
  name: p.name,
  kind: p.kind,
  category: p.category,
  barcode: p.barcode || "",
  price: String(p.price),
  cost: String(p.cost || ""),
  stock: String(p.stock),
  daily_stock: p.daily_stock == null ? "" : String(p.daily_stock),
  image_url: p.image_url || "",
  images: p.images?.length ? p.images : p.image_url ? [p.image_url] : [],
  unit: p.unit,
  weight: p.weight || "",
  low_stock_threshold: String(p.low_stock_threshold),
  is_new: !!p.is_new,
  description: p.description || "",
  description_ja: p.description_ja || "",
  name_ja: p.name_ja || "",
});

export default function Stok() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("grocery");
  const [onlyNoPhoto, setOnlyNoPhoto] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const bulkToday = async (payload, msg) => {
    try {
      const { data } = await api.post("/products/today-bulk", payload);
      setSelected([]);
      load();
      toast.success(`${msg} (${data.updated} menu)`);
    } catch (e) {
      toast.error(errMsg(e, "Gagal memperbarui menu hari ini"));
    }
  };

  const load = useCallback(async () => setProducts((await api.get("/products")).data), []);
  useEffect(() => {
    load();
  }, [load]);

  const list = useMemo(
    () =>
      products.filter(
        (p) => p.kind === tab && (!onlyNoPhoto || !p.image_url) && matchesQuery(p, q)
      ),
    [products, tab, onlyNoPhoto, q]
  );
  const noPhotoCount = products.filter((p) => p.kind === tab && !p.image_url).length;
  const grouped = useMemo(
    () =>
      Object.entries(
        list.reduce((acc, p) => {
          const key = p.category || "Lainnya";
          (acc[key] = acc[key] || []).push(p);
          return acc;
        }, {})
      ).sort(([a], [b]) => a.localeCompare(b, "id")),
    [list]
  );
  const lowCount = products.filter((p) => p.track_stock && p.stock <= p.low_stock_threshold).length;

  const suggest = (field, kind) =>
    [
      ...new Set(
        products.filter((p) => (kind ? p.kind === kind : true)).map((p) => p[field]).filter(Boolean)
      ),
    ].sort((a, b) => String(a).localeCompare(String(b), "id"));

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Nama & harga wajib diisi");
    const payload = {
      name: form.name,
      kind: form.kind,
      category: form.category || "Lainnya",
      barcode: form.barcode || null,
      price: Number(form.price),
      ...(isOwner ? { cost: Number(form.cost) || 0 } : {}),
      stock: Math.max(Number(form.stock) || 0, 0),
      unit: form.unit || "pcs",
      weight: /bento/i.test(form.category) ? "" : form.weight.trim(),
      low_stock_threshold: Number(form.low_stock_threshold) || 0,
      daily_stock: form.daily_stock === "" ? null : Number(form.daily_stock),
      image_url: form.image_url || null,
      images: (form.images || []).filter(Boolean).slice(0, 3),
      is_new: !!form.is_new,
      description: form.description || "",
      description_ja: form.description_ja || "",
      name_ja: form.name_ja || "",
      track_stock: true,
    };
    try {
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
    } catch (e) {
      return toast.error(errMsg(e, "Gagal menyimpan produk"));
    }
    setOpen(false);
    setEditing(null);
    setForm(empty);
    load();
    toast.success(editing ? "Produk diperbarui" : "Produk ditambahkan");
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold">{t("stok_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("stok_sub")}</p>
        </div>
        <Button
          data-testid="add-product-btn"
          onClick={() => {
            setForm({ ...empty, kind: tab });
            setEditing(null);
            setOpen(true);
          }}
        >
          <Plus size={16} className="mr-1" /> {t("new_product")}
        </Button>
      </div>

      {lowCount > 0 && (
        <div
          className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm"
          data-testid="low-stock-alert"
        >
          <AlertTriangle size={16} /> {lowCount} {t("low_stock_warn")}
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <input
            data-testid="stok-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama, kategori, atau barcode…"
            className="w-full h-10 rounded-full border border-gray-200 bg-white pl-10 pr-9 text-sm outline-none focus:border-primary"
          />
          {q && (
            <button
              data-testid="stok-search-clear"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 grid place-items-center rounded-full text-muted-foreground hover:bg-gray-100"
            >
              ×
            </button>
          )}
        </div>
        {q && (
          <span className="text-sm text-muted-foreground" data-testid="stok-search-count">
            {list.length} barang ditemukan
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {[
          ["grocery", t("tab_grocery")],
          ["menu", t("tab_menu")],
        ].map(([k, label]) => (
          <button
            key={k}
            data-testid={`stok-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
              tab === k ? "bg-gray-900 text-white" : "bg-white border border-gray-200"
            }`}
          >
            {label}
          </button>
        ))}
        <button
          data-testid="filter-no-photo-btn"
          onClick={() => setOnlyNoPhoto((v) => !v)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
            onlyNoPhoto ? "bg-amber-500 text-white" : "bg-white border border-gray-200"
          }`}
        >
          {t("photo_missing")} · {noPhotoCount}
        </button>
      </div>

      <AiFillPanel onDone={load} />
      {tab === "menu" && (
        <div
          className="flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3"
          data-testid="today-bulk-bar"
        >
          <span className="text-sm text-amber-900 font-medium">Menu Hari Ini</span>
          <span className="text-xs text-amber-800" data-testid="today-selected-count">
            {selected.length} dipilih
          </span>
          <div className="flex flex-wrap gap-2 ml-auto">
            <Button
              size="sm"
              variant="outline"
              data-testid="today-mark-selected-btn"
              disabled={selected.length === 0}
              onClick={() =>
                bulkToday({ ids: selected, available: true }, "Ditandai menu hari ini")
              }
            >
              Tandai terpilih
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid="today-unmark-selected-btn"
              disabled={selected.length === 0}
              onClick={() =>
                bulkToday({ ids: selected, available: false }, "Dihapus dari menu hari ini")
              }
            >
              Hapus terpilih
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid="today-select-all-btn"
              onClick={() =>
                setSelected(selected.length === list.length ? [] : list.map((p) => p.id))
              }
            >
              {selected.length === list.length && list.length > 0
                ? "Batal pilih semua"
                : "Pilih semua"}
            </Button>
            <Button
              size="sm"
              data-testid="today-mark-all-btn"
              onClick={() => {
                if (
                  window.confirm(
                    "Tandai SEMUA menu resto (termasuk yang tidak tampil karena filter) sebagai Menu Hari Ini?"
                  )
                )
                  bulkToday({ all_menu: true, available: true }, "Semua menu ditandai");
              }}
            >
              Tandai semua menu
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive"
              data-testid="today-clear-all-btn"
              onClick={() => {
                if (window.confirm("Kosongkan daftar Menu Hari Ini?"))
                  bulkToday({ all_menu: true, available: false }, "Menu hari ini dikosongkan");
              }}
            >
              Kosongkan
            </Button>
          </div>
        </div>
      )}
      <CategoryManager kind={tab} groups={grouped} onDone={load} />

      <StockTable
        grouped={grouped}
        isEmpty={list.length === 0}
        onlyNoPhoto={onlyNoPhoto}
        onReload={load}
        selectable={tab === "menu"}
        selected={selected}
        onToggle={toggleSelect}
        onEdit={(p) => {
          setForm(formOf(p));
          setEditing(p.id);
          setOpen(true);
        }}
      />

      <ProductDialog
        open={open}
        setOpen={setOpen}
        form={form}
        setForm={setForm}
        editing={editing}
        isOwner={isOwner}
        suggest={suggest}
        onSave={save}
        onSaved={load}
      />
    </div>
  );
}
